import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import ErrorBoundary from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Home from "@/pages/Home";
import Portfolio from "@/pages/Portfolio";
import ProjectDetail from "@/pages/ProjectDetail";
import Process from "@/pages/Process";
import About from "@/pages/About";
import CityPage from "@/pages/CityPage";
import Enquire from "@/pages/Enquire";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import AuthCallback from "@/components/AuthCallback";
import { AuthProvider } from "@/lib/auth";

function SmoothScroll() {
  const lenisRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    window.__lenis = lenis;
    let raf = 0;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function Shell() {
  const location = useLocation();
  const { pathname } = location;
  const isAdmin = pathname.startsWith("/admin");

  // Emergent Auth returns to {redirect}#session_id=... — exchange it before any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/portfolio/:slug" element={<ProjectDetail />} />
        <Route path="/process" element={<Process />} />
        <Route path="/about" element={<About />} />
        <Route path="/interiors/:citySlug" element={<CityPage />} />
        <Route path="/enquire" element={<Enquire />} />
        <Route path="/account" element={<Account />} />
        <Route path="/admin" element={<Admin />} />
        <Route
          path="*"
          element={
            <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-obsidian px-6 text-center">
              <p className="eyebrow">404</p>
              <p className="font-serif text-4xl text-zinc-100">This room was never rendered.</p>
              <Link to="/" className="btn-gold" data-testid="notfound-home-link">
                Back to home
              </Link>
            </div>
          }
        />
      </Routes>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppFloat />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <SmoothScroll />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
